import {PreventionInvalidityCode} from "../preventative-interactions";
import {BaseImportTransformer, toPartNumberUpper} from "./shared";

/**
 * Parses a non-negative amount from a mapped row value.
 *
 * @param {*} value
 * @returns {number | null} Parsed amount, or null when missing/invalid/negative
 */
const parseNonNegativeAmount = (value) => {
    if (value === undefined || value === null || value === '')
        return null;
    const amount = parseFloat(value.toString());
    if (Number.isNaN(amount) || amount < 0)
        return null;
    return amount;
};

/**
 * Product flat shipping rates — phased validation (see {@link BaseImportTransformer} run contract).
 * Columns: `partNo`, `amount`. API payload matches `Api.savePartRate`.
 */
export class ProductFlatShippingRatesImportTransformer extends BaseImportTransformer {
    /**
     * @param {Array<Object|null|undefined>} mappedData
     * @param {boolean} isFinalRun
     */
    async run(mappedData, isFinalRun = false) {
        const rows = mappedData ?? [];
        const preventativeIssues = [];

        /** @type {{index: number, item: *, partNoUpper: string, amount: number | null, codes: string[]}[]} */
        const rowState = rows.map((item, index) => ({
            index,
            item,
            partNoUpper: toPartNumberUpper(item?.partNo),
            amount: parseNonNegativeAmount(item?.amount),
            codes: [],
        }));

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (!rs.partNoUpper) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        const uniqueParts = [...new Set(rowState.map((r) => r.partNoUpper).filter(Boolean))].sort();
        /** @type {Record<string, *>} */
        let partIds = {};
        /** @type {Record<string, *>} */
        let partInfo = {};
        if (uniqueParts.length > 0) {
            const bundle = await this.ctx.fetchPartInfoByPartNumbers(uniqueParts);
            partIds = bundle.partIds;
            partInfo = bundle.partInfo;
        }

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.partNoUpper && !partIds[rs.partNoUpper]) {
                rs.codes.push(PreventionInvalidityCode.INVALID_PART);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) continue;
            if (rs.amount === null) {
                rs.codes.push(PreventionInvalidityCode.INVALID_AMOUNT);
            }
        }

        for (const rs of rowState) {
            if (!rs.item) {
                preventativeIssues.push({
                    rowIndex: rs.index,
                    originalRow: rows[rs.index],
                    invalidityCodes: [PreventionInvalidityCode.INVALID_PART],
                });
                continue;
            }
            if (!rs.codes.length) continue;
            preventativeIssues.push({
                rowIndex: rs.index,
                originalRow: rs.item,
                invalidityCodes: [...new Set(rs.codes)],
            });
        }

        const issueIndexSet = new Set(preventativeIssues.map((p) => p.rowIndex));

        const apiRequests = rows.map((item, index) => {
            if (!item) {
                return isFinalRun
                    ? null
                    : {
                        originalPartId: null,
                        partNumber: undefined,
                        parentCategoryId: null,
                        parentCategoryTitle: null,
                        amount: null,
                    };
            }

            const rs = rowState[index];
            const partId = partIds[rs.partNoUpper];
            const info = partInfo[rs.partNoUpper];
            const hasIssue = issueIndexSet.has(index);

            if (hasIssue) {
                return isFinalRun
                    ? null
                    : {
                        originalPartId: partId ?? null,
                        partNumber: info?.partNo ?? item.partNo,
                        parentCategoryId: info?.parentCategoryId ?? null,
                        parentCategoryTitle: info?.parentCategoryTitle ?? null,
                        amount: rs.amount,
                    };
            }

            return {
                originalPartId: Number(partId),
                partNumber: info?.partNo ?? item.partNo,
                parentCategoryId: info?.parentCategoryId ?? null,
                parentCategoryTitle: info?.parentCategoryTitle ?? null,
                amount: rs.amount,
            };
        });

        return {apiRequests, preventativeIssues};
    }
}
